import { useDocumentVisibility } from "@react-hookz/web";
import { useCallback, useEffect } from "react";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import dayjs from "dayjs";
import { logger } from "@artis/logger";
dayjs.extend(timezone);
dayjs.extend(utc);

const RESTART_CHANNEL = "app-restart";
/** Bundles from before the rename still listen on this name; posted to as well so they reload too. */
const LEGACY_RESTART_CHANNEL = "month-roll-restart";

/** Reloads every open window: posts a restart message for the others, then reloads this one. */
export const restartAllWindows = () => {
  for (const name of [RESTART_CHANNEL, LEGACY_RESTART_CHANNEL]) {
    const channel = new BroadcastChannel(name);
    channel.postMessage({ type: "restart" });
    channel.close();
  }
  window.location.reload();
};

/** Invokes the callback on restart messages from either channel; returns a cleanup. */
export const listenForRestart = (onRestart: () => void) => {
  const channels = [RESTART_CHANNEL, LEGACY_RESTART_CHANNEL].map((name) => new BroadcastChannel(name));
  const handler = (event: MessageEvent) => {
    if (event?.data?.type === "restart") onRestart();
  };
  for (const channel of channels) channel.addEventListener("message", handler);
  return () => {
    for (const channel of channels) {
      channel.removeEventListener("message", handler);
      channel.close();
    }
  };
};

/**
 * This provider reloads the page on a daily basis and on month roll.
 * We refresh on month rollover because grid values are cached by month offset and as such all values in the worker need to be refreshed.
 */
export const DailyReloadProvider = ({ children }: { children: React.ReactNode }) => {
  const visible = useDocumentVisibility();

  const toggleReload = useCallback(() => {
    logger.log("Running daily reload");
    restartAllWindows();
  }, []);

  useEffect(
    () =>
      listenForRestart(() => {
        logger.log("Received reload message, reloading page");
        window.location.reload();
      }),
    [],
  );

  useEffect(() => {
    const store = createLocalStorageStore("user-refresh", defaultValue());
    if (visible && checkShouldReload(store)) toggleReload();
    const interval = setInterval(
      () => {
        if (checkShouldReload(store)) toggleReload();
      },
      1_000 * 10, // 10 seconds
    );
    return () => clearInterval(interval);
  }, [toggleReload, visible]);

  return children;
};

const JITTER = 1_000 * 60 * 30; // 30 minutes
const defaultValue = () => ({
  jitter: Math.floor(Math.random() * JITTER),
  lastRefresh: dayjs().tz("Europe/London").valueOf(),
});

/**
 * Should refresh every day after LONDON timezone 01:30 + jitter
 */
export const checkShouldReload = (store: Store<{ jitter: number; lastRefresh: number }>) => {
  const { jitter, lastRefresh } = store.get();

  const now = dayjs().tz("Europe/London");
  const last = dayjs.tz(lastRefresh, "Europe/London");

  // Build today's target: 01:00 London + jitter (ms)
  const todayThreshold = dayjs().tz("Europe/London").startOf("day").add(1, "hour").add(jitter, "millisecond").valueOf();
  const dailyThreshold = now.valueOf() >= todayThreshold && last.valueOf() < todayThreshold;

  if (dailyThreshold) {
    store.set({ jitter, lastRefresh: now.valueOf() });
    return true;
  }

  return false;
};

type Store<T> = {
  get: () => T;
  set: (value: T) => void;
};

export const createLocalStorageStore = <T,>(key: string, defaultValue: T): Store<T> => {
  const get = () => {
    try {
      const text = localStorage.getItem(key) as string;
      if (text === null) return defaultValue;
      const value = JSON.parse(localStorage.getItem(key) as string) as {
        value: T;
      };
      if (value === undefined) return defaultValue;
      return value.value;
    } catch (_error) {
      return defaultValue;
    }
  };
  const set = (value: T) => localStorage.setItem(key, JSON.stringify({ value }));
  return { get, set };
};
